import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  deliveryAreasApi, occasionsApi, ringListApi,
  type DeliveryArea, type DeliveryOccasion, type RingListItem,
} from '../crmApi'
import { ApiError } from '../api'

export function RingList() {
  const navigate = useNavigate()
  const [areas, setAreas] = useState<DeliveryArea[]>([])
  const [areaId, setAreaId] = useState<number | null>(null)
  const [occasions, setOccasions] = useState<DeliveryOccasion[]>([])
  const [occasionId, setOccasionId] = useState<number | null>(null)
  const [rows, setRows] = useState<RingListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const fail = (e: unknown) => setError(e instanceof ApiError ? `Fel ${e.status}: ${e.body}` : String(e))

  // Load delivery areas once.
  useEffect(() => {
    deliveryAreasApi.list()
      .then((a) => { setAreas(a); setError(null); if (a.length) setAreaId((id) => id ?? a[0].id) })
      .catch(fail)
  }, [])

  // Load upcoming occasions whenever the area changes.
  useEffect(() => {
    if (areaId == null) return
    setOccasions([]); setOccasionId(null); setRows([])
    occasionsApi.list(areaId, true)
      .then((o) => { setOccasions(o); setError(null); if (o.length) setOccasionId(o[0].id) })
      .catch(fail)
  }, [areaId])

  // Load the ring list whenever the occasion changes.
  useEffect(() => {
    if (occasionId == null) { setRows([]); return }
    void reload(occasionId)
  }, [occasionId])

  async function reload(id: number) {
    setLoading(true)
    try { setRows(await ringListApi.list(id)); setError(null) }
    catch (e) { fail(e) }
    finally { setLoading(false) }
  }

  async function registerAttempt(item: RingListItem) {
    if (occasionId == null) return
    setBusy(item.customerId); setNotice(null)
    try {
      const r = await ringListApi.registerAttempt(item.customerId, occasionId)
      setNotice(r.removedFromList
        ? `${displayName(item)} har nu ${r.failedAttempts} misslyckade försök och tas bort från listan.`
        : `Misslyckat försök registrerat för ${displayName(item)} (${r.failedAttempts} st).`)
      await reload(occasionId)
    } catch (e) { fail(e) } finally { setBusy(null) }
  }

  async function registerNegative(item: RingListItem) {
    if (occasionId == null) return
    setBusy(item.customerId); setNotice(null)
    try {
      const r = await ringListApi.registerNegative(item.customerId, occasionId)
      setNotice(r.autoInactivated
        ? `${displayName(item)} flaggades som ointresserad för 5:e gången och har inaktiverats automatiskt (§6.21).`
        : `${displayName(item)} flaggad som ointresserad (${r.negativeResponseStreak} av 5).`)
      await reload(occasionId)
    } catch (e) { fail(e) } finally { setBusy(null) }
  }

  return (
    <div>
      <h1>Ringlista</h1>
      <p className="muted">Rundringning inför ett leveranstillfälle (§6.20). Sorterad med minst antal försök överst.</p>
      {error && <p className="error">{error}</p>}
      {notice && <p className="muted">{notice}</p>}

      <div className="card filters">
        <label>
          Leveransområde{' '}
          <select value={areaId ?? ''} onChange={(e) => setAreaId(Number(e.target.value))}>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label>
          Leveranstillfälle{' '}
          <select
            value={occasionId ?? ''}
            disabled={occasions.length === 0}
            onChange={(e) => setOccasionId(Number(e.target.value))}
          >
            {occasions.length === 0 && <option value="">Inga kommande tillfällen</option>}
            {occasions.map((o) => <option key={o.id} value={o.id}>{o.date}{o.isClosed ? ' (stängt)' : ''}</option>)}
          </select>
        </label>
      </div>

      {loading ? <p className="muted">Laddar…</p> : occasionId == null ? null : (
        <table className="grid">
          <thead>
            <tr>
              <th>Namn</th><th>Telefon</th><th>Ringanteckning</th>
              <th>Saldo</th><th>Försök</th><th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.customerId}>
                <td>{displayName(c)}</td>
                <td>{phones(c)}</td>
                <td>{c.ringNote}</td>
                <td>{c.balance.toFixed(2)} kr</td>
                <td>{c.failedAttempts}</td>
                <td className="actions">
                  <button disabled={busy === c.customerId} onClick={() => void registerAttempt(c)}>Misslyckat försök</button>
                  <button disabled={busy === c.customerId} onClick={() => void registerNegative(c)}>Ointresserad</button>
                  <button onClick={() => navigate(`/customers/${c.customerId}`)}>Kundkort</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="muted">Inga kunder att ringa för det här tillfället.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  )
}

function displayName(c: RingListItem) {
  return `${c.firstName} ${c.lastName ?? ''}`.trim()
}

function phones(c: RingListItem) {
  return [c.homePhone, c.mobilePhone, c.workPhone].filter(Boolean).join(', ')
}
